<template>
  <div>

    <error-dialog :status="loadStatus" />
    <error-dialog :status="reloadOrderStatus" />
    <error-dialog :status="replaceDesignStatus" />
    <error-dialog :status="saveStatus">
      Sorry, something went wrong trying to save your changes. Please try again later.
    </error-dialog>

    <tenor-dialog
      v-if="showGiphy"
      v-model="showGiphy"
      @select="v => quick.media.push({ w: parseInt(v.w), h: parseInt(v.h), url: v.url, gif: true })"
    />

    <dialog-scroll-lock
      v-if="showGiftcardPayment"
      width="500"
      :value="true"
      @input="showGiftcardPayment.resolve(false)"
    >
      <v-card class="d-flex flex-column">
        <v-btn class="ma-3" style="right: 0;" absolute icon @click="showGiftcardPayment.resolve(false)">
          <v-icon v-html="mdiClose" />
        </v-btn>
        <v-card-title>Add gift card</v-card-title>
        <payment
          class="pb-4 mx-auto"
          style="min-height: 414px;"
          :metadata="showGiftcardPayment.metadata"
          @complete="showGiftcardPayment.resolve(true)"
        />
      </v-card>
    </dialog-scroll-lock>

    <!-- Note that we set 'is-completed' to false so we can control when the payment happens (in the 'submitQuick' function) -->
    <giftcard-dialog
      v-if="showGiftcard"
      v-model="showGiftcard"
      :order-id="orderId"
      :order="order"
      :is-completed="false"
      :max-value="maxAddGiftcardValue"
      @select="v => quick.media.push({ w: 300, h: 190, url: v.product_img, giftcard: v })"
    />

    <input v-show="false" ref="image" type="file" accept="image/*,image/heic" @click.stop />

    <dialog-scroll-lock
      v-if="showPreview"
      fullscreen
      :transition="false"
      :value="true"
      @input="$router.push($routeAssign({ hash: undefined }))"
    >
      <card-open
        v-if="order"
        class="absolute-fill"
        :preview="order"
        :block="true"
        @close="$router.push($routeAssign({ hash: undefined }))"
      />
    </dialog-scroll-lock>

    <group-video-status-dialog
      :status="groupVideoStatus"
      @continue="groupVideoStatus = null; !isStandaloneGroupVideo && loadGroupVideo();"
      @close="() => {
        if (isStandaloneGroupVideo)
          tryClose();
        else
          groupVideoStatus = null;
      }"
    >
      <div v-if="!isStandaloneGroupVideo" class="pt-6">
        Click the <v-btn class="mx-1 px-2" style="vertical-align: inherit;" rounded small @click="groupVideoStatus = null; showInfo = true;"><v-icon class="mr-1" x-small v-html="mdiHelp" /> Help</v-btn> button at the top of the screen, or find the video on page {{groupVideoPageI + 1}} of the card to reopen the project.
      </div>
    </group-video-status-dialog>

    <dialog-scroll-lock v-if="showInfo && !canSign" v-model="showInfo" width="500">
      <v-card dark class="warning">
        <v-btn class="float-right ma-3" icon @click="showInfo = false">
          <v-icon v-html="mdiClose" />
        </v-btn>
        <v-card-title>
          <v-icon class="mr-2" color="white" v-html="mdiLock" />
          <div v-if="order.type == 'GROUP'">This card is locked</div>
          <div v-else>This is not a group card</div>
        </v-card-title>
        <v-card-text v-if="order.user_id == globalUserId" class="white--text text-body-1">
          <router-link class="white--text" :to="{ name: (order.state == 'PENDING') ? 'project' : 'account-orders', params: { orderId } }">Manage this card from your account</router-link>
        </v-card-text>
        <v-card-text v-else class="white--text text-body-1">
          <span>Only the organizer can make changes.</span>
          <span v-if="(order.type == 'GROUP') && (order.lock != 'FORCE')"> Please contact the organizer to unlock it.</span>
        </v-card-text>

        <!-- TODO: this probably isn't the right place to have this now that we can redirect to the #design page -->
        <template v-if="global.profile?.org_admin && (global.profile.org_id == order.org_id)">
          <v-divider />
          <v-card-text class="pt-0">
            <v-switch hide-details label="Edit as owner" v-model="editAsOwner">
              <template v-slot:label>
                <span class="v-card__text pa-0">Edit as owner <v-btn icon @click.stop="$confirm({ title: 'Edit as owner', msg: 'As an organization administrator, you can toggle this switch to make edits to the card as if you were the one who created it.', primary: 'Ok', buttons: [ 'Ok' ] })"><v-icon v-html="mdiHelpCircle" /></v-btn></span>
              </template>
            </v-switch>
          </v-card-text>
        </template>
      </v-card>
    </dialog-scroll-lock>

    <dialog-scroll-lock
      v-else-if="showInfo"
      :persistent="canSign && !myPageIndexes.length && !didCloseInfo"
      :width="showAddQuick ? 440 : 360"
      v-model="showInfo"
    >
      <v-expand-transition>
        <v-card v-if="showAddQuick">
          <v-list key="add-quick-back">
            <v-list-item class="primary--text li-pop simple" @click="showAddQuick = false">
              <v-icon style="transform: rotate(180deg);" v-html="mdiChevronRight" />
              <div class="pl-0">Back</div>
            </v-list-item>
          </v-list>
          <div class="px-6 pt-1 pb-5">
            <!-- We explicitly set the min-width to avoid a transition artifact when the buttons wrap -->
            <div class="ma-n1" style="min-width: calc(100vw - 88px);">
              <v-btn class="ma-1 px-2 white--text" small color="cyan" :disabled="(quick.media.length >= 9) || (quick.media.filter(x => x.gif).length >= 4)" @click="showGiphy = true">
                <v-icon class="mr-1" small v-html="mdiStickerEmoji" />
                <span>Add GIF</span>
              </v-btn>
              <v-btn class="ma-1 px-2 white--text" small color="indigo" :disabled="(quick.media.length >= 9) || (quick.media.filter(x => x.upload).length >= 3)" @click="promptUpload">
                <v-icon class="mr-1" small v-html="mdiImage" />
                <span>Add picture</span>
              </v-btn>
              <template v-if="(maxAddGiftcardValue > 0) && (isOwner || !order.skin?.disable_contributor_giftcards)">
                <v-menu v-if="giftcardBlobsEditable.length" offset-y width="320" max-width="320">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn v-on="on" v-bind="attrs" class="ma-1 px-2 white--text" small color="green lighten-2" :disabled="(quick.media.length >= 9) || !!quick.media.find(x => x.giftcard)">
                      <v-icon class="mr-1" small v-html="mdiWalletGiftcard" />
                      <span>Add gift card</span>
                      <v-icon class="menu-down mr-n1" small v-html="mdiMenuDown" />
                    </v-btn>
                  </template>
                  <v-list class="primary--text text-body-2" dense>
                    <v-list-item-subtitle class="text--label-active px-3 pt-1 pb-2">
                      Add value to existing gift card
                    </v-list-item-subtitle>
                    <v-list-item
                      v-for="g in giftcardBlobsEditable" :key="'add-quick-' + g.id"
                      class="li-pop simple px-3"
                      @click="showGiftcardBlob = g"
                    >
                      <v-img
                        class="v-card v-sheet mr-0"
                        v-bind:style="{ opacity: g.giftcard.claim_ids?.length ? .5 : 1 }"
                        width="42" min-width="42" max-width="42"
                        :aspect-ratio="300 / 190"
                        :src="g.giftcard.product_img"
                      />
                      <div
                        class="pl-0"
                        v-bind:class="{ 'grey--text text--lighten-1': g.giftcard.claim_ids?.length }"
                        style="max-width: calc(100% - 66px);"
                      >
                        <div
                          class="overline short"
                          style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis;"
                          v-text="g.giftcard.product_name"
                        />
                        <div class="text--label-active" style="color: inherit;">
                          <currency :code="g.giftcard.cur" />{{g.giftcard.amt}}
                        </div>
                      </div>
                      <v-icon small v-html="mdiChevronRight" />
                    </v-list-item>
                    <v-divider class="my-2" />
                    <v-list-item class="li-pop simple px-3 py-4" @click="showGiftcard = true">
                      <v-icon style="padding: 0 9px;" v-html="mdiWalletGiftcard" />
                      <div class="px-0">Add new gift card</div>
                      <v-icon small v-html="mdiChevronRight" />
                    </v-list-item>
                  </v-list>
                </v-menu>
                <v-btn v-else class="ma-1 px-2 white--text" small color="green lighten-2" :disabled="!!quick.media.find(x => x.giftcard)" @click="showGiftcard = true">
                  <v-icon class="mr-1" small v-html="mdiWalletGiftcard" />
                  <span>Add gift card</span>
                </v-btn>
              </template>
            </div>

            <div class="my-3 mx-n3" style="column-count: 3; column-gap: 0;">
              <div v-for="{ url, w, h, giftcard }, i in quick.media" class="relative pa-3 mx-auto">
                <v-card>
                  <img class="d-block fill-width" v-bind:style="{ 'aspect-ratio': w / h }" :src="url">
                </v-card>
                <v-btn style="top: 0; right: 0;" x-small fab absolute @click="quick.media.splice(i, 1)">
                  <v-icon v-html="mdiClose" />
                </v-btn>
                <v-btn v-if="giftcard" style="bottom: 0; left: 0;" small color="green lighten-2" absolute rounded dark>
                  <currency :code="giftcard.cur" />
                  {{giftcard.amt}}
                  <span v-if="(order.n_giftcard_recipients || 1) > 1" class="ml-1 grey--text text--lighten-2">×{{order.n_giftcard_recipients}}</span>
                </v-btn>
              </div>
            </div>
            <v-form ref="quick-form" @submit.prevent>
              <v-textarea
                class="mt-3 inline-error white"
                outlined dense auto-grow rows="5" label="Message" persistent-placeholder
                :rules="[ v => !!v || 'Required' ]"
                v-model="quick.msg"
              />
              <ul class="text--label-active mt-3 mb-3 ml-n2">
                <li>Don't forget to include your name</li>
                <li>You can still edit your message later, including the font, color, and layout</li>
              </ul>
              <div class="text-right">
                <v-btn color="primary" @click="submitQuick">
                  Submit <v-icon class="ml-1 mr-n1" v-html="mdiChevronRight" />
                </v-btn>
              </div>
            </v-form>
            <!--
            Your message has been saved - you can see what it looks like and make changes
            -->
          </div>
        </v-card>

        <v-card v-else class="pt-6">
          <v-btn
            v-if="!(canSign && !myPageIndexes.length && !didCloseInfo)"
            style="top: 12px; right: 12px;"
            absolute icon
            @click="showInfo = false"
          >
            <v-icon v-html="mdiClose" />
          </v-btn>

          <div class="mx-auto relative" style="aspect-ratio: 7 / 5; max-width: 200px;">
            <envelope
              style="position: absolute; inset: -20% 0 -20% 0;"
              :skin="order.skin"
              :from="order.info.from"
              :to="to"
            />
          </div>

          <v-card-title class="pt-6">Sign this group card</v-card-title>
          <v-card-subtitle v-if="order.msg" class="pb-3 font-italic" style="max-width: 360px;">
            {{order.msg}}
          </v-card-subtitle>

          <v-list class="primary--text">
            <v-list-item v-if="!myPageIndexes.length" class="li-pop simple pr-5" @click="showAddQuick = true">
              <v-icon v-html="mdiMessageFlash" />
              <div>
                <div>Add a quick message</div>
                <div class="text--label-active" style="color: inherit;">With automatic layout</div>
              </div>
              <v-icon v-html="mdiChevronRight" />
            </v-list-item>

            <template v-if="(maxAddGiftcardValue > 0) && (isOwner || !order.skin?.disable_contributor_giftcards)">
              <v-list-item
                v-if="giftcardBlobsEditable.length == 1"
                class="li-pop simple pr-5"
                @click="showGiftcardBlob = giftcardBlobsEditable[0]"
              >
                <div class="v-icon v-icon-container">
                  <v-img
                    class="v-card v-sheet"
                    width="42" min-width="42" max-width="42"
                    :aspect-ratio="300 / 190"
                    :src="giftcardBlobsEditable[0].giftcard.product_img"
                  />
                </div>
                <div>
                  <div>Add to group gift card</div>
                  <div
                    class="text--label-active"
                    style="color: inherit; text-transform: uppercase; letter-spacing: 0.1667em !important;"
                    v-text="giftcardBlobsEditable[0].giftcard.product_name"
                  />
                </div>
                <v-icon v-html="mdiChevronRight" />
              </v-list-item>
              <v-menu v-else-if="giftcardBlobsEditable.length" offset-x width="320" max-width="320">
                <template v-slot:activator="{ on, attrs }">
                  <v-list-item v-on="on" v-bind="attrs" class="li-pop simple pr-5">
                    <v-icon v-html="mdiWalletGiftcard" />
                    <div>
                      <div>Add to group gift cards</div>
                      <div class="text--label-active" style="color: inherit;">{{giftcardBlobsEditable.length}} gift cards</div>
                    </div>
                    <v-icon v-html="mdiChevronRight" />
                  </v-list-item>
                </template>
                <v-list class="primary--text text-body-2" dense>
                  <v-list-item
                    v-for="g in giftcardBlobsEditable" :key="'add-top-level-' + g.id"
                    class="li-pop simple px-3"
                    @click="showGiftcardBlob = g"
                  >
                    <v-img
                      class="v-card v-sheet mr-0"
                      v-bind:style="{ opacity: g.giftcard.claim_ids?.length ? .5 : 1 }"
                      width="42" min-width="42" max-width="42"
                      :aspect-ratio="300 / 190"
                      :src="g.giftcard.product_img"
                    />
                    <div
                      class="pl-0"
                      v-bind:class="{ 'grey--text text--lighten-1': g.giftcard.claim_ids?.length }"
                      style="max-width: calc(100% - 66px);"
                    >
                      <div
                        class="overline short"
                        style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis;"
                        v-text="g.giftcard.product_name"
                      />
                      <div class="text--label-active" style="color: inherit;">
                        <currency :code="g.giftcard.cur" />{{g.giftcard.amt}}
                      </div>
                    </div>
                    <v-icon small v-html="mdiChevronRight" />
                  </v-list-item>
                  <v-divider class="my-2" />
                  <v-list-item
                    class="li-pop simple px-3 py-4"
                    @click="autoSwipeToNewPage(); $refs['card-editor'].showGiftcard = true; showInfo = false;"
                  >
                    <v-icon style="padding: 0 9px;" v-html="mdiWalletGiftcard" />
                    <div class="px-0">Add new gift card</div>
                    <v-icon small v-html="mdiChevronRight" />
                  </v-list-item>
                </v-list>
              </v-menu>
              <v-list-item
                v-else
                class="li-pop simple pr-5"
                @click="autoSwipeToNewPage(); $refs['card-editor'].showGiftcard = true; showInfo = false;"
              >
                <v-icon v-html="mdiWalletGiftcard" />
                <div>Add gift card</div>
                <v-icon v-html="mdiChevronRight" />
              </v-list-item>
            </template>

            <v-list-item v-if="groupVideoBlob" class="li-pop simple pr-5" @click="loadGroupVideo">
              <v-icon v-html="mdiVideoVintage" />
              <div>
                <div>Add to group video</div>
                <div class="text--label-active" style="color: inherit;">On page {{groupVideoPageI + 1}}</div>
              </div>
              <v-icon v-html="mdiChevronRight" />
            </v-list-item>

            <v-list-item
              v-if="!myPageIndexes.length"
              class="li-pop simple pr-5"
              @click="autoSwipeToNewPage(); showInfo = false;"
            >
              <v-icon v-html="mdiPencil" />
              <div>
                <div>Sign the card directly</div>
                <div class="text--label-active" style="color: inherit;">For complete editorial control</div>
              </div>
              <v-icon v-html="mdiChevronRight" />
            </v-list-item>

            <v-list-item
              v-else-if="myPageIndexes.length == 1"
              class="li-pop simple pr-5"
              @click="
                showInfo = false;
                $refs['card-editor'].swipe(myPageIndexes[0] - $refs['card-editor'].selectedPageI);
              "
            >
              <v-icon v-html="mdiMagnify" />
              <div>
                <div>Show your message</div>
                <div class="text--label-active" style="color: inherit;">You already added to this card</div>
              </div>
              <v-icon v-html="mdiChevronRight" />
            </v-list-item>

            <v-menu v-else offset-x width="200" max-width="200">
              <template v-slot:activator="{ on, attrs }">
                <v-list-item v-on="on" v-bind="attrs" class="li-pop simple pr-5">
                  <v-icon v-html="mdiMagnify" />
                  <div>
                    <div>Show your messages</div>
                    <div class="text--label-active" style="color: inherit;">You already added to this card</div>
                  </div>
                  <v-icon v-html="mdiChevronRight" />
                </v-list-item>
              </template>
              <v-list class="primary--text text-body-2" dense>
                <v-list-item
                  v-for="i in myPageIndexes" :key="'signed-page-' + i"
                  class="li-pop simple px-3"
                  @click="showInfo = false; $refs['card-editor'].swipe(i - $refs['card-editor'].selectedPageI);"
                >
                  <card-preview
                    class="inline-page-preview v-card v-sheet"
                    style="max-width: 42px;"
                    :mode="canSign ? 'edit' : 'view'"
                    :card="{
                      ...order,
                      pages: [
                        {
                          ...order.pages[i],
                          blobs: order.pages[i].blobs.slice().map(
                            x => ({ ...x, opacity: (x.user_id == userId) ? 1 : .5 })
                          )
                        }
                      ]
                    }"
                    :width="42"
                  />
                  <div>Page {{i + 1}}</div>
                  <v-icon small v-html="mdiChevronRight" />
                </v-list-item>
              </v-list>
            </v-menu>
          </v-list>
          <v-divider class="mt-6" />
          <div class="px-5">
            <v-switch dense hide-details color="grey darken-2" label="Show button labels" v-model="global.showHelp">
              <template v-slot:label>
                <span class="v-card__text pa-0 pl-2">Show button labels</span>
              </template>
            </v-switch>
            <!-- This option is obsolete now that owners edit cards from the #design step -->
            <!--
            <v-switch
              v-if="(!isOwner || editAsOwner) && global.profile?.org_admin && (global.profile.org_id == order.org_id)"
              dense hide-details color="grey darken-2" label="Edit as owner"
              v-model="editAsOwner"
            >
              <template v-slot:label>
                <span class="v-card__text pa-0 pl-2">Edit as owner <v-btn class="mt-n1" icon @click.stop="$confirm({ title: 'Edit as owner', msg: 'As an organization administration, you can toggle this switch to make edits to the card as if you were the one who created it.', primary: 'Ok', buttons: [ 'Ok' ] })"><v-icon v-html="mdiHelpCircle" /></v-btn></span>
              </template>
            </v-switch>
            -->
          </div>
          <v-list class="text-body-2" style="color: rgba(0, 0, 0, 0.6);">
            <v-list-item v-if="!order.skin.hide_account" class="li-pop simple pr-5" @click="showSignIn = true">
              <template v-if="isSignedIn">
                <v-icon v-html="mdiAccountCheck" />
                <div>
                  <div>Signed in</div>
                  <div class="text--label-active" style="color: inherit;">{{global.profile.email}}</div>
                </div>
                <v-icon v-html="mdiLogout" />
              </template>
              <template v-else>
                <v-icon v-html="mdiAccount" />
                <div>
                  <div>Sign in</div>
                  <div class="text--label-active" style="color: inherit;">Sign in to revisit your changes later</div>
                  <!-- <div class="text--label-active grey--text text--lighten-1">Optional</div> -->
                </div>
                <v-icon v-html="mdiLogin" />
              </template>
            </v-list-item>
            <v-list-item
              class="li-pop simple pr-5"
              @click="$router.push($routeAssign({ hash: '#preview' }));"
            >
              <v-icon v-html="mdiEmailOpen" />
              <div>
                <div>Preview</div>
                <div class="text--label-active" style="color: inherit;">See what {{order.multiple_recipients ? 'a' : 'the'}} recipient will see</div>
              </div>
              <v-icon v-html="mdiChevronRight" />
            </v-list-item>
          </v-list>
        </v-card>
      </v-expand-transition>
    </dialog-scroll-lock>

    <sign-in-dialog
      ref="sign-in"
      v-model="showSignIn"
      :allow-anonymous="!isStandaloneGroupVideo"
      :transition="false"
      :skin="order ? order.skin : {}"
    >
      <template v-slot:title v-if="isStandaloneGroupVideo">
        Sign in to add content
      </template>
      <template v-slot:subtitle>
        <template v-if="isStandaloneGroupVideo">We use your email address (A) so we have a record of who added what content, and (B) so you can manage your content after it's been saved.</template>
        <template v-else>Sign in with your email address so the organizer has a record of who contributed to the {{orderType}} and so you can edit your content later</template>
      </template>
    </sign-in-dialog>

    <giftcard-add-dialog
      v-if="showGiftcardBlob"
      :order-id="orderId"
      :order="order"
      :blob="showGiftcardBlob"
      :locked="!canSign"
      :max-value="maxAddGiftcardValue"
      @input="showGiftcardBlob = null"
    />

    <dialog-scroll-lock
      transition="" fullscreen content-class="white overflow-x-hidden" persistent no-click-animation
      :value="true"
    >

      <template v-if="!isStandaloneGroupVideo && showGroupVideo && groupVideoBlob">
        <group-video-playback
          v-if="order.lock"
          dialog mode="open"
          :project-id="groupVideoBlob.group_video.project_id"
          v-model="showGroupVideo"
          @slow-playback="onSlowPlayback"
        />
        <group-video-editor
          v-else
          :project-id="groupVideoBlob.group_video.project_id"
          :order="order"
          v-model="showGroupVideo"
          @input="(value, status) => { showInfo = false; groupVideoStatus = status; }"
        />
      </template>

      <div
        class="fill-height overflow-hidden"
        style="min-height: max(100%, calc(480px + 73px));"
        v-bind:style="{
          'background-color': order ? order.skin.page_background_color : null
        }"
      >

        <div v-if="!global.profile || loadStatus.pending" class="fill-height d-flex justify-center align-center">
          <v-progress-circular indeterminate />
        </div>

        <template v-else-if="order">

          <card-background :src="order.skin.page_background_img" />

          <div class="header d-flex align-start pa-1">
            <div class="flex-grow-0 fill-height relative">
              <img v-if="order.skin.header_logo" class="absolute" height="100%" :src="order.skin.header_logo" />
              <img v-else class="absolute" height="100%" src="/ellacard-logo-filled-307x160.png" alt="Ellacard" title="Ellacard" />
            </div>
            <v-spacer />

            <v-btn
              v-if="viewAs"
              class="ma-2"
              color="primary" elevation="5" large rounded
              @click="$refs['card-editor'].viewAs = null"
            >
              <v-icon class="ml-n1 mr-2" v-html="iconPathCardPages" />
              View as book
            </v-btn>

            <div
              v-else
              class="ma-2 v-btn--is-elevated v-btn--fab btn-group btn-group-horizontal"
              style="border-radius: 28px; z-index: 1;"
            >
              <v-btn v-if="!isStandaloneGroupVideo" elevation="0" large rounded @click="showInfo = true">
                <v-icon class="ml-n1 mr-1" small v-html="mdiHelp" />
                Help
              </v-btn>
              <v-btn elevation="0" large rounded color="primary" @click="tryClose">
                <div class="ml-n1 mr-1 relative">
                  <v-progress-circular v-if="dirty || saveStatus.pending" size="16" width="1" indeterminate />
                  <v-icon v-else small v-html="mdiCheck" />
                </div>
                Done
              </v-btn>
            </div>
          </div>

          <div class="body">
            <div
              v-if="isStandaloneGroupVideo"
              class="relative fill-height mx-auto px-md-3 pb-md-3" style="max-width: 1200px;"
            >
              <div class="relative fill-height">
                <v-card class="absolute-fill" tile>
                  <group-video-editor
                    ref="group-video-editor"
                    class="fill-height"
                    block
                    :project-id="groupVideoBlob.group_video.project_id"
                    :order="order"
                  />
                </v-card>
              </div>
            </div>
            <card-editor
              v-else
              ref="card-editor"
              :order-id="orderId"
              :card="order"
              :is-owner="isOwner"
              :mode="canSign ? 'edit' : 'view'"
              :has-group-video="!!groupVideoBlob"
              :max-giftcard-value="maxAddGiftcardValue"
              :allow-replace-design="order && order.allow_replace_design"
              :is-verified-recipient="order && order.verified_recipient"
              :show-help="global.showHelp"
              @input="dirty = true"
              @insert-blob="onInsertOrUpdate"
              @update-blob="onInsertOrUpdate"
              @update-layers="x => didUpdateLayersForPageIds.add(x)"
              @insert-page="pageId => addedPageIds.push(pageId)"
              @delete-blob="blob => { removedBlobIds.push(blob.id); enqueueSave(false); }"
              @delete-page="page => { removedPageIds.push(page.id); enqueueSave(false); }"
              @view-as="x => viewAs = x"
              @click-giftcard="x => showGiftcardBlob = x"
              @click-group-video="showGroupVideo = true"
              @click-edit-form="$router.push({ name: 'account-orders', params: { orderId }, hash: '#event' })"
              @page-group-video="() => { if (!didShowGroupVideo && !order.lock) showGroupVideo = true; }"
              @replace-design="replaceDesign"
              @reload="reloadOrder"
              @save="enqueueSave(true)"
            />

          </div>
        </template>

        <div v-else-if="notFound && !isRedirecting" class="d-flex flex-column justify-center align-center pa-4 fill-height">
          <div class="title">Card not found</div>
          <v-btn class="mt-8" :to="{ name: 'start' }">Home</v-btn>
          <v-btn class="mt-8" :to="{ name: 'catalog' }">Browse cards</v-btn>
          <div class="mt-8 text-center">
            Think you're seeing this page in error? Please let us know!<br>
            <a href="mailto:support@ellacard.com">support@ellacard.com</a>
          </div>
        </div>

      </div>
    </dialog-scroll-lock>
  </div>
</template>


<style scoped>
@import '@/styles/step-list.css';

.header {
  height: 73px;
  width: 100%;
  z-index: 2;
  pointer-events: none;
}
.header :not(.spacer) {
  pointer-events: auto;
}

.body {
  position: relative;
  height: calc(100% - 73px);
  min-height: max(480px, 100% - 73px);
  z-index: 0;
}

.li-pop .v-image,
.li-pop .inline-page-preview {
  transition: transform .3s;
}
.li-pop .v-icon.v-icon-container {
  width: 24px;
  max-width: 24px;
}
.li-pop:hover .v-icon.v-icon-container {
  transform: none;
}
.li-pop:hover .v-image {
  transform: scale(1.3);
}
.li-pop:hover .inline-page-preview {
  transform: scale(1.2);
}
</style>


<script>
import CardBackground from '@/components/CardBackground.vue';
import CardEditor from '@/components/CardEditor.vue';
import CardPreview from '@/components/CardPreview.vue';
import Currency from '@/components/Currency.vue';
import Envelope from '@/components/Envelope.vue';
import ErrorDialog from '@/components/ErrorDialog.vue';
import GiftcardAddDialog from '@/components/GiftcardAddDialog.vue';
import GiftcardDialog from '@/components/GiftcardDialog.vue';
import TenorDialog from '@/components/TenorDialog.vue';
import GroupVideoStatusDialog from '@/components/GroupVideoStatusDialog.vue';
import Payment from '@/components/Payment.vue';
import ShareBtn from '@/components/ShareBtn.vue';
import SignInDialog from '@/components/SignInDialog.vue';
import { orgSkin } from '@/mixins/org-skin.js';
import { publicDesign } from '@/mixins/public-design.js';
import undoSideEffects from '@/mixins/undo-side-effects.js';
import getBlobMediaFiles from '@/utils/card-media.js';
import { processImageFile } from '@/utils/image.js';
import modernize from '@/utils/order-modernize.js';
import getRecipients from '@/utils/recipients.js';
import { isBlobVisible, mergeBlobs, findOpenToPageIndex } from '@/utils/edit-order.js';
import { findGroupVideoBlob, findGroupVideoBlobPageI, getOrderType } from '@/utils/group-video.js';
import { getAverageColor, getRandomContrastingColor } from '@/utils/group-video-template.js';
import { scale } from '@/utils/image.js';
import { DEFAULT as DEFAULT_SKIN } from '@/utils/skin.js';
import uuid4 from '@/utils/uuid.js';
import { fonts, colors, frames, lightColors, textSizeRange } from '@/card-customization-options.js';
import iconPathCardPages from 'raw-loader!@/assets/icon-path-card-pages.txt';
import { mdiHelp, mdiClose, mdiCheck, mdiAlert, mdiAlertCircle, mdiLock, mdiPencil, mdiFileFind, mdiHelpCircle, mdiMessageFlash, mdiChevronRight, mdiMagnify, mdiImage, mdiStickerEmoji, mdiWalletGiftcard, mdiMenuDown, mdiAccount, mdiAccountCheck, mdiLogin, mdiLogout, mdiVideoVintage, mdiEmailOpen } from '@mdi/js';


const AUTOSAVE_DELAY_MS = 3000;


function postMessageOnClose() {
  // Legacy event
  window.top.postMessage(JSON.stringify({ url: location.href }), '*');

  $event.log('embed-msg', { src: 'ellacard-embed', key: 'sign:close', data: {} });
  window.top.postMessage(JSON.stringify({ src: 'ellacard-embed', key: 'sign:close', data: {} }), '*');
}


function isUUID(str) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(str || '');
}


export default {
  name: 'card-sign',

  routerViewMeta: {
    $hide: [ 'header', 'footer' ],
    title: 'Sign this card',
    description: 'Add messages, images, videos, and more to this card'
  },

  components: {
    CardBackground,
    CardEditor,
    CardPreview,
    Currency,
    Envelope,
    ErrorDialog,
    GiftcardAddDialog,
    GiftcardDialog,
    TenorDialog,
    GroupVideoStatusDialog,
    Payment,
    ShareBtn,
    SignInDialog,
    CardOpen: () => import('@/views/CardOpen.vue'),
    GroupVideoEditor: () => import('@/components/GroupVideoEditor.vue'),
    GroupVideoPlayback: () => import('@/components/GroupVideoPlayback.vue')
  },

  mixins: [ orgSkin, publicDesign, undoSideEffects ],

  props: [ 'orderId', 'back' ],

  data() {
    return {
      inviteId: isUUID(this.$route.query.ref) ? this.$route.query.ref : undefined,
      notFound: false,
      dirty: false,
      order: null,
      quick: { media: [], msg: '' },
      showInfo: false,
      showAddQuick: false,
      showAddQuickStatus: false,
      showSignIn: false,
      addedPageIds: [],
      removedBlobIds: [],
      removedPageIds: [],
      didCloseInfo: false,
      didUpdateBlobIds: new Set(),
      groupVideoStatus: null,
      showGiftcardBlob: null,
      showGiphy: false,
      showGiftcard: false,
      showGiftcardPayment: null,
      showGroupVideo: false,
      didShowGroupVideo: false,
      showPreview: false,
      editAsOwner: false,
      viewAs: null,
      isRedirecting: false,
      didUpdateLayersForPageIds: new Set(),
      saveStatus: this.$track('save'),
      loadStatus: this.$track('load'),
      reloadOrderStatus: this.$track('reloadOrder'),
      setOrderStatus: this.$track('setOrder'),
      replaceDesignStatus: this.$track('replaceDesign')
    };
  },

  computed: {
    myPageIndexes() {
      return (this.order?.pages || []).map((x, i) => x.blobs.find(y => y.user_id == this.userId) ? i : -1).filter(x => x!= -1);
    },

    loadTrigger() {
      return this.$route.query?.org + this.$route.query?.skin + this.orderId;
    },

    to() {
      return this.order.multiple_recipients ? 'Multiple recipients' : this.order.info.to;
    },

    canSign() {
      return this.order && (this.order.state == 'COMPLETED') && (this.isOwner || (this.order.type == 'GROUP' && !this.order.lock));
    },

    nPeople() {
      const users = new Set();
      for (const page of (this.order && this.order.pages || []))
        for (const blob of page.blobs)
          blob.user_id && users.add(blob.user_id);
      return users.size;
    },

    groupVideoPageI() {
      return this.order && findGroupVideoBlobPageI(this.order);
    },

    groupVideoBlob() {
      return this.order && findGroupVideoBlob(this.order);
    },

    orderType() {
      return this.order && getOrderType(this.order);
    },

    isStandaloneGroupVideo() {
      return this.orderType == 'video';
    },

    isOwner() {
      return (this.order?.user_id == this.userId) || (this.order.allow_universal_editing && !this.order.lock && !this.order.skin.private_pages);
    },

    giftcardBlobs() {
      if (!this.order)
        return [];
      const blobs = [];
      for (const page of this.order.pages)
        for (const blob of page.blobs)
          if ((blob.type == 'GIFTCARD') && (!this.order.skin.private_pages || this.isOwner || (blob.user_id == this.userId) || this.isVerifiedRecipient))
            blobs.push(blob);
      return blobs;
    },

    giftcardBlobsEditable() {
      if (!this.order)
        return [];
      if (this.isOwner)
        return this.giftcardBlobs;
      if (this.order.skin?.disable_contributor_giftcards)
        return [];
      if (this.order.skin?.hide_giftcard_totals || this.order.skin?.private_pages)
        return this.giftcardBlobs.filter(x => x.user_id == this.userId);
      return this.giftcardBlobs;
    },

    maxAddGiftcardValue() {
      const o = this.order;
      const profile = this.global.profile;
      let total = 0;
      for (const blob of this.giftcardBlobs)
        total += blob.giftcard.amt;
      if ((!o.org_id && (profile?.user_id == o.user_id)) || ((profile?.org_id == o.org_id) && profile?.org_admin))
        // For org orders, only org admins can add gift cards / value
        // For non-org orders, only the order's owner can add gift cards / value
        return 500000 - total;
      if (o.hide_giftcards)
        return 0;
      if (o.skin.max_giftcard_value == 0)
        return 0;
      return (o.skin.max_giftcard_value || 500000) - total;
    },

    userId() {
      if (this.global.profile?.org_admin && (this.global.profile.org_id == this.order.org_id) && this.editAsOwner)
        return this.order.user_id;
      return this.globalUserId;
    }
  },

  created() {
    Object.assign(this, {
      iconPathCardPages,
      mdiHelp,
      mdiClose,
      mdiCheck,
      mdiAlert,
      mdiAlertCircle,
      mdiLock,
      mdiPencil,
      mdiFileFind,
      mdiHelpCircle,
      mdiMessageFlash,
      mdiChevronRight,
      mdiMagnify,
      mdiImage,
      mdiStickerEmoji,
      mdiWalletGiftcard,
      mdiMenuDown,
      mdiAccount,
      mdiAccountCheck,
      mdiLogin,
      mdiLogout,
      mdiVideoVintage,
      mdiEmailOpen
    });
  },

  async beforeRouteLeave(to, from, next) {
    if (!this.isSignedIn && !this.order?.lock)
      await this.$refs['sign-in'].prompt();
    next();
  },

  methods: {
    async loadOrder() {
      if (!isUUID(this.orderId))
        return null;
      try {
        const order = await this.$call('get-order', { order_id: this.orderId, invite_id: this.inviteId });
        modernize(order);

        if (!this.IS_IFRAME && order.base_url) {
          this.isRedirecting = true;
          const url = new URL(order.base_url);
          url.searchParams.set('q', `sign/${this.orderId}`);
          location.href = url;
          return;
        }

        if (order.state == 'CANCELLED')
          return null;

        // It's possible to override an order's skin by passing either an org ID (with custom branding) or a serialized
        // skin itself
        const q = this.$route.query;
        let skin;
        if (q.org)
          skin = await this.getOrgSkin(q.org);
        else if (q.skin)
          try {
            skin = JSON.parse(decodeURIComponent(q.skin));
          } catch (e) {
            console.warn('Error unpacking skin');
          }
        if (skin)
          Object.assign(order.skin, _.omitBy(skin, _.isNull));

        // We could use this just in case we're missing some skin properties (shouldn't be possible)
        // order.skin = Object.assign({}, DEFAULT_SKIN, order.skin);

        if (!order.read_token)
          // This property is used by the card-open preview and will not exist for those who do not have priveleges to
          // manage the order
          order.read_token = this.orderId;

        return order;
      } catch (e) {
        if (e.type == 'NotFound')
          return null;
        else
          throw e;
      }
    },

    async load() {
      this.didCloseInfo = false;
      this.autoAddedPageId = null;
      this.notFound = false;
      this.order = null;
      this.showInfo = false;
      this.showGroupVideo = false;
      this.didShowGroupVideo = false;
      const order = await this.loadOrder();
      if (order) {
        await this.setOrder(order);
        this.addedPageIds = [];
        this.removedBlobIds = [];
        this.removedPageIds = [];
        this.showInfo = !order.demo && !this.back && !this.isStandaloneGroupVideo && (this.$route.hash != '#preview');
      } else {
        this.notFound = true;
      }
      await this.$nextTick();
      this.didUpdateBlobIds = new Set();
      this.dirty = false;
    },

    autoSwipeToNewPage() {
      const editor = this.$refs['card-editor'];
      if (!this.canSign || this.isOwner || !editor)
        return;
      if (!this.autoAddedPageId)
        this.autoAddedPageId = editor.addPage(this.order.pages.length - 1, { blobs: [] }).id;
      const i = this.order.pages.findIndex(x => x.id == this.autoAddedPageId);
      editor.swipe(i - editor.selectedPageI);
    },

    async reloadOrder() {
      let selectedPageId;
      if (this.$refs['card-editor']) {
        this.$refs['card-editor'].selected = null;
        selectedPageId = this.$refs['card-editor'].selectedPage.id;
      }

      try {
        const order = await this.$call('get-order', { order_id: this.orderId });
        if (order.state == 'CANCELLED') {
          this.order = null;
          return;
        }
        this.setOrder(order);
      } catch (e) {
        if (e.type == 'NotFound') {
          this.order = null;
          return;
        }
        throw e;
      }
      await this.$nextTick();
      this.didUpdateBlobIds = new Set();
      this.addedPageIds = [];
      this.removedBlobIds = [];
      this.removedPageIds = [];
      this.dirty = false;

      if (this.$refs['card-editor']) {
        const i = this.order.pages.findIndex(x => x.id == selectedPageId);
        if (i != -1)
          this.$refs['card-editor'].setSelectedPageI(i);
      }
    },

    onInsertOrUpdate(pageI, blob) {
      if (this.setOrderStatus.pending)
        return;
      if (!this.isOwner && !this.order.allow_universal_editing && (blob.user_id != this.userId))
        // Note that it IS possible to trigger a change to a blob we don't own, such as when adding to a giftcard. But
        // in that case, we're simply updating the blob to reflect the server state - we don't actually have permission
        // to update it via the update-order-pages API anyway.
        // Also note, you had experimented with adding the following:
        // && (!this.inviteId || (this.inviteId != blob.ref))
        return;
      if (this.inviteId)
        blob.ref = this.inviteId;
      this.didUpdateBlobIds.add(blob.id);
      this.enqueueSave(false);
    },

    async enqueueSave(immediate) {
      clearTimeout(this.saveTimeout);
      if (this.order.demo && !this.isOwner)
        return;
      if (!immediate) {
        this.saveTimeout = setTimeout(() => this.enqueueSave(true), AUTOSAVE_DELAY_MS);
        return;
      }
      // In case the 'save' function takes longer to run than our auto-save delay
      while (this.saveStatus.pending)
        await this.saveStatus.promise;
      await this.save();
    },

    async setOrder(order) {
      // TODO: This forces an update to every blob, we could be much more efficient and only update the blobs / pages
      // that we needed to. Could cause performance issues for large cards?
      await this.undoSideEffects([ 'dirty', 'didUpdateBlobIds' ], () => this.order = order);
    },

    async save(explicit=false) {
      if (!this.dirty || !this.canSign)
        // This function triggers the 'dirty' watcher, even though we set it immediately back, so we need to guard
        // against hyperactive enqueuing
        return;

      if (this.order.demo && !this.isOwner) {
        if (explicit)
          await this.$confirm({
            title: 'Demo',
            msg: 'This is a demo – your changes cannot be saved.',
            buttons: [ 'Ok' ],
            primary: 'Ok',
            persistent: false
          });
        return;
      }

      // Only extract MY blobs / pages - no need to redundantly send all the other crap to the server
      const page_order = this.isOwner ? [] : undefined;
      const pages = [];
      for (const page of this.order.pages) {
        if (this.isOwner)
          page_order.push(page.id);
        if (this.addedPageIds.includes(page.id)) {
          if ((page.id != this.autoAddedPageId) || page.blobs.length)
            pages.push(page);
          continue;
        }
        const blobs = page.blobs.filter(x => this.didUpdateBlobIds.has(x.id) && (this.isOwner || this.order.allow_universal_editing || !x.user_id || (x.user_id == this.userId)));
        const blob_order = this.didUpdateLayersForPageIds.has(page.id) ? page.blobs.map(x => x.id) : undefined;
        if (blobs.length > 0 || blob_order)
          pages.push({ id: page.id, blobs, blob_order });
        for (const blob of blobs)
          if (this.inviteId && (blob.user_id == this.global.profile.user_id))
            blob.ref = this.inviteId;
      }

      // Make sure to set this upfront so any changes that happen while in the process of saving also trigger
      this.dirty = false;
      this.didUpdateBlobIds = new Set();
      this.didUpdateLayersForPageIds = new Set();

      if (!page_order && !pages.length && !this.removedBlobIds.length && !this.removedPageIds.length)
        return;

      const files = await getBlobMediaFiles({ ...this.order, pages });
      const params = {
        order_id: this.orderId,
        invite_id: this.inviteId,
        pages,
        files,
        remove_blob_ids: this.removedBlobIds,
        remove_page_ids: this.removedPageIds,
        page_order
      };
      if (this.editAsOwner)
        params.as_owner = true;
      if (this.isOwner)
        params.background = this.order.background;
      this.addedPageIds = [];
      this.removedBlobIds = [];
      this.removedPageIds = [];
      const saved = await this.$call('update-order-pages', params);

      if (!this.isOwner && saved.lock) {
        // This order was locked while we were editing it
        this.didUpdateBlobIds = new Set();
        this.setOrder(saved);
        if (this.$refs['card-editor']) {
          this.$refs['card-editor'].selected = null;
          this.$refs['card-editor'].setSelectedPageI(0);
        }
        this.showInfo = true;
        this.dirty = false;
        return;
      }

      if (this.dirty)
        // This order was modified while we were were saving it (don't merge anything)
        return;

      // The server did the hard work of merging orders, but we need to make sure that we stay on the same selected
      // page ID with the same selected blob object (if applicable)
      const curSelectedBlob = this.$refs['card-editor']?.selected;
      if (curSelectedBlob) {
        for (const page of saved.pages) {
          for (let i = 0; i < page.blobs.length; i++) {
            if (page.blobs[i].id == curSelectedBlob.id) {
              page.blobs[i] = curSelectedBlob;
              break;
            }
          }
        }
      }

      const curSelectedPageId = this.$refs['card-editor']?.selectedPage?.id;
      this.setOrder(saved);
      await this.$nextTick();
      let newSelectedPageI = saved.pages.findIndex(x => x.id == curSelectedPageId);
      if (newSelectedPageI != -1)
        this.$refs['card-editor'].setSelectedPageI(newSelectedPageI);

      if (this.showAddQuickStatus) {
        this.$notify(`Message has been saved – select ${(this.$refs['card-editor'].selectedPage.blobs.length == 1) ? 'it' : 'an element'} to make any adjustments`);
        this.showAddQuickStatus = false;
      } else {
        this.$notify('Changes have been saved');
      }
    },

    async tryClose() {
      if (this.canSign && this.isStandaloneGroupVideo && !this.groupVideoStatus) {
        this.groupVideoStatus = this.$refs['group-video-editor'].getStatus();
        return;
      }
      if (this.dirty)
        await this.save();
      this.close();
    },

    close() {
      if (this.$route.query.done == 'post-message')
        postMessageOnClose();
      else if (this.back)
        this.$router.back();
      else if (this.order.skin.done_url) {
        const params = {
          user_id: this.userId || '',
          order_id: this.orderId,
          delivery_id: '',
          design_id: this.order ? this.order.design_id : '',
          view: 'sign'
        };
        let url = this.order.skin.done_url;
        for (const [ key, value ] of Object.entries(params))
          url = url.replace(`{${key}}`, value);
        window.open(url, '_top')
      } else {
        // No harm in posting a message here, and it protects the case where an embedded page does not have a done_url
        // set up
        postMessageOnClose();
        if (this.global.profile?.user_id == this.order?.user_id)
          this.$router.push({ name: 'account-orders', params: { orderId: this.orderId } });
        else
          // Go straight to the catalog instead of the start page since the signer already knows how the cards work
          this.$router.push({ name: 'catalog' });
      }
    },

    async loadGroupVideo() {
      if (!this.isStandaloneGroupVideo)
        await this.$refs['card-editor'].swipe(this.groupVideoPageI - this.$refs['card-editor'].selectedPageI);
      this.showGroupVideo = true;
    },

    async replaceDesign(design_id) {
      if (design_id == this.order.design_id)
        return;
      const order = await this.$call('user-replace-order-design', { order_id: this.orderId, design_id });
      this.setOrder(order);
      await this.$nextTick();
      this.$refs['card-editor'].setSelectedPageI(0);
    },

    onSlowPlayback() {
      if (this.didShowSlowPlayback)
        return;
      this.didShowSlowPlayback = true;
      // Note that this can only be called when 'canSign' is false (so it doesn't overlap with the same method in the
      // group-video-editor, and it will never be shown to the organizer)
      this.$confirm({ title: 'Slow playback detected', msg: `Don't worry, this is only a <b>preview</b> of your video. We're currently working on building a smooth, high-quality version. Click the download button, and we'll automatically send you an email when it's ready.`, buttons: [ 'Ok' ], primary: 'Ok' });
    },

    async processImage(file) {
      const media = await processImageFile(file);
      this.quick.media.push({ ...media, upload: true });
    },

    promptUpload() {
      const el = this.$refs.image;
      const onChange = e => {
        if (e.target.files[0])
          this.processImage(e.target.files[0]);
        e.target.onchange = null;
        e.target.value = null;
      };
      el.onchange = onChange;
      el.click();
    },

    async submitQuick() {
      if (!this.$refs['quick-form'].validate())
        return;

      if (this.dirty)
        await this.enqueueSave(true);

      const editor = this.$refs['card-editor'];
      const media = this.quick.media;

      let page = this.order.pages.last.blobs.length ? null : this.order.pages.last;
      let giftcardBlob;
      const { giftcard } = media.find(x => x.giftcard) || {};
      if (giftcard) {
        const didPay = await new Promise(
          r => {
            this.showGiftcardPayment = {
              resolve: r,
              metadata: {
                target: 'GIFTCARD',
                order_id: this.orderId,
                page_id: page?.id,
                amt: giftcard.amt,
                country: giftcard.country,
                src: giftcard.src,
                product_id: giftcard.product_id
              }
            };
          }
        );
        if (!didPay)
          return;

        await this.reloadOrder();
        await this.$nextTick();
        page = this.order.pages.slice().reverse().find(
          x => (x.blobs.length == 1) && (x.blobs[0].type == 'GIFTCARD') && (x.blobs[0].user_id == this.userId)
        );
        giftcardBlob = page.blobs[0];
      } else if (!page) {
        page = editor.addPage(this.order.pages.length, { blobs: [] });
      }

      // Open to the last page so that editor.insertBlob works as expected
      editor.swipe(this.order.pages.length - 1 - this.$refs['card-editor'].selectedPageI);

      const rows = {
        upload: { proportion: media.find(x => x.upload) ? 3 : 0 },
        giftcard: { proportion: media.find(x => x.giftcard) ? 3 : 0 },
        gif: { proportion: media.find(x => x.gif) ? 1.5 : 0 },
        text: {
          proportion: [ [ 60, 2 ], [ 120, 3 ], [ 240, 4 ], [ Number. MAX_VALUE, 5 ] ].find(x => x[0] > this.quick.msg.length)[1]
        }
      };
      const proportionTotal = Object.values(rows).reduce((a, row) => a + row.proportion, 0);

      let rowY = 0;
      for (const key of _.shuffle([ 'upload', 'giftcard', 'gif', 'text' ])) {
        const rowH = 840 * (rows[key].proportion / proportionTotal);
        rows[key].y = rowY;
        rows[key].h = rowH;
        rowY += rowH;
      }

      const uploads = media.filter(x => x.upload);
      if (uploads.length) {
        const frame = frames.filter(x => x)[ Math.floor(Math.random() * (frames.length - 1)) ];
        const maxW = 600 / uploads.length;
        const rs = [ [], [ 0 ], [ -5, 5 ], [ -5, 0, 5 ] ][uploads.length];
        const xs = [ [], [ 300 ], [ 180, 420 ], [ 120, 300, 480 ] ][uploads.length];
        for (let i = 0; i < uploads.length; i++) {
          const upload = uploads[i];
          const { w, h } = scale(upload.w, upload.h, .8 * maxW, .8 * rows.upload.h, 'cover');
          editor.insertBlob({
            id: uuid4(),
            user_id: this.userId,
            r: rs[i],
            w,
            h,
            x: xs[i],
            y: rows.upload.y + rows.upload.h / 2,
            type: 'IMG',
            img: { src: upload.url, frame }
          });
        }
      }

      const gifs = media.filter(x => x.gif);
      if (gifs.length) {
        const maxW = 600 / gifs.length;
        let rNeg = (gifs.length == 1) ? 0 : ((Math.random() < .5) ? 5 : -5);
        for (let i = 0; i < gifs.length; i++) {
          const gif = gifs[i];
          const { w, h } = scale(gif.w * 100, gif.h * 100, maxW, .8 * rows.gif.h, 'cover');
          editor.insertBlob({
            id: uuid4(),
            user_id: this.userId,
            r: rNeg * Math.random(),
            w,
            h,
            x: (i + 1) * 600 / (gifs.length + 1),
            y: rows.gif.y + rows.gif.h / 2,
            type: 'IMG',
            img: { src: gif.url, frame: null }
          });
          rNeg *= -1;
        }
      }

      if (giftcardBlob) {
        giftcardBlob.x = 300;
        giftcardBlob.y = rows.giftcard.y + rows.giftcard.h / 2;
        giftcardBlob.h = Math.min(rows.giftcard.h, 212.5);
        giftcardBlob.w = (337 / 212.5) * h;
        page.blobs.splice(page.blobs.indexOf(giftcardBlob), 1);
        page.blobs.push(giftcardBlob);

        // In general, I try to use methods directly on the 'editor' itself, but this deviates from that pattern
        this.didUpdateBlobIds.add(giftcardBlob.id);
        this.didUpdateLayersForPageIds.add(editor.selectedPage.id);
      }

      const maxH = (rows.text.proportion / 5) * 840;
      const w = Math.min(600, (Math.max(3, rows.text.proportion) / 5) * 600) - 20;
      const h = Math.min(maxH, (rows.text.proportion / proportionTotal) * 840) - 20;
      editor.insertBlob({
        id: uuid4(),
        user_id: this.userId,
        r: 0,
        w,
        h,
        x: 300,
        y: rows.text.y + rows.text.h / 2,
        type: 'TEXT',
        text: {
          font: fonts[ Math.floor(Math.random() * fonts.length) ],
          color: getRandomContrastingColor(await getAverageColor(this.order.background), colors),
          size: 480,
          align: 'center',
          valign: 'center',
          text: this.quick.msg,
          css: { 'line-height': 1.2 }
        }
      });

      editor.selected = null;

      this.quick = { media: [], msg: '' };
      this.showAddQuick = false;
      this.showInfo = false;
      this.showAddQuickStatus = true;
      this.dirty = true;
      this.enqueueSave(true);
    },

    onSlowPlayback() {
      if (this.didShowSlowPlayback)
        return;
      this.didShowSlowPlayback = true;
      this.$confirm({ title: 'Slow playback detected', msg: `Don't worry, this is only a <b>preview</b> of your video. We're currently working on building a smooth, high-quality version.`, buttons: [ 'Ok' ], primary: 'Ok' });
    }
  },

  watch: {
    loadTrigger: {
      immediate: true,
      handler() {
        this.load();
      }
    },

    'global.jwt': {
      immediate: true,
      async handler(v) {
        if (!this.global.jwt)
          this.global.jwt = await this.$call('get-jwt', { method: 'ANON' });
      }
    },

    'global.profile': {
      immediate: true,
      handler(cur, prev) {
        if (cur && prev && !cur.anon && prev.anon)
          // We just switched from an anon account to an actual user account, which may have affected some of the
          // existing user IDs in our order
          this.reloadOrder();
      }
    },

    dirty(v) {
      if (v)
        this.enqueueSave(false);
    },

    showInfo(v) {
      if (!v)
        this.didCloseInfo = true;
    },

    showGroupVideo(v) {
      if (v)
        this.didShowGroupVideo = true;
    },

    showGiftcardBlobAllowedValues(v) {
      if (v) {
        if (v.fixed)
          this.giftcardAmt = v.fixed[0];
        else
          this.giftcardAmt = 5 > v.range.max ? v.range.max : 5;
      }
    },

    '$route.hash': {
      immediate: true,
      async handler(v) {
        // We always wait to make sure the preview is on top of the main card-editor dialog
        await this.$nextTick();
        this.showPreview = v == '#preview';
      }
    },

    // inviteId: {
    //   immediate: true,
    //   handler(v) {
    //     if (v)
    //       this.$router.replace(this.$routeAssign({ query: _.omit(this.$route.query, 'ref') }));
    //   }
    // }
  }
}
</script>
